import { Package } from '@features/DynamicPackagesConfigurationWizard/types/Package.schema';
import { getPackage } from '@features/DynamicPackagesConfigurationWizard/useGetPackage';
import { getStaticRate } from '@features/rates';
import { Rate } from '@features/rates/types';
import { FIVE_MINUTES } from '@features/state/QueryClient/QueryClient';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { Property } from 'shared-types/Property';
import { TimeSlotsType } from 'shared-types/SpaPackage';
import { getSpaPackageTimeslots } from './useGetSpaPackageTimeslots';

export const PackagesSpaAvailabilityKey = {
  packagesSpaAvailability: ['packagesSpaAvailability'] as const,
  getPackagesSpaAvailability: (
    propertyId: number,
    rates: string[],
    journeyParams: ParsedJourneyParams
  ) =>
    [
      ...PackagesSpaAvailabilityKey.packagesSpaAvailability,
      propertyId,
      rates,
      journeyParams,
    ] as const,
};

export const usePackagesSpaAvailability = (
  property: Property,
  rates: Rate[],
  journeyParams: ParsedJourneyParams,
  enabled = true
) => {
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: PackagesSpaAvailabilityKey.getPackagesSpaAvailability(
      property.id,
      rates.map((rate) => rate.id),
      journeyParams
    ),
    queryFn: () => getPackagesSpaAvailability(property, rates, journeyParams),
    staleTime: FIVE_MINUTES,
    cacheTime: FIVE_MINUTES,
    enabled,
  });
};

export const getPackagesSpaAvailability = async (
  property: Property,
  rates: Rate[],
  journeyParams: ParsedJourneyParams
) => {
  const numberOfGuests = journeyParams.adults + journeyParams.children;

  const newAvailability = await Promise.all(
    rates.map(async (rate) => {
      //this is required when viewing rates in the single room detail page as the rates only have codes there
      if (!rate.uuid) {
        const { data: rateWithUuid } = await getStaticRate({
          rateCode: rate.id,
          siteSlug: property.slug,
        });
        rate.uuid = rateWithUuid._id;
      }

      if (!rate.uuid || rate.unAvailable) return null;
      let packageData: Package | undefined;
      try {
        packageData = await getPackage({
          rateId: rate.uuid,
          propertyUuid: property.uuid,
        });
      } catch (error) {
        return null;
      }

      if (!packageData) return null;

      let timeslots: TimeSlotsType = {};
      let isUnavailable = false;

      const spaPackageId = Number(packageData.meta?.entityId);
      if (!spaPackageId) {
        return { rate, packageData, timeslots, isUnavailable };
      }

      const numberOfNights = moment(journeyParams.toDate).diff(
        moment(journeyParams.fromDate),
        'days'
      );
      const packageDurationNights = packageData?.numberOfNights;
      const isValidStayLength =
        packageDurationNights && packageDurationNights !== 1
          ? packageDurationNights === numberOfNights
          : true;

      if (!isValidStayLength) {
        return { rate, packageData, timeslots, isUnavailable: true };
      }

      try {
        timeslots = await getSpaPackageTimeslots({
          spaPackageId,
          startDate: moment(journeyParams.fromDate).format('YYYY-MM-DD'),
          quantity: numberOfGuests,
          propertyId: property.id,
          numberOfDays: moment(journeyParams.toDate).diff(moment(journeyParams.fromDate), 'days'),
        });

        const timeslot = Object.values(timeslots || [])?.[0]?.[0];
        isUnavailable = timeslot === undefined;
      } catch (error) {
        isUnavailable = true;
      }
      return { rate, packageData, timeslots, isUnavailable };
    })
  );
  return newAvailability.reduce((acc: Record<string, (typeof newAvailability)[number]>, data) => {
    if (data) acc[data.rate.id] = data;
    return acc;
  }, {});
};
